import {useParams} from "react-router-dom";
import {SyntheticEvent, useCallback, useEffect, useMemo, useState} from "react";
import {API} from "../../types/API";
import {AxiosError, AxiosResponse} from "axios";
import {TempTransactionResponse} from "../../types/Responses/TempTransactionResponse";
import {Button, Typography} from "@material-tailwind/react";
import GenericInputs from "../../components/GenericInputs";
import {useDispatch, useSelector} from 'react-redux'
import {validateData} from "../../reducers/validationReducer";
import {TransactionResponse} from "../../types/Responses/TransactionResponse";
import {useErrorHandler} from "react-error-boundary";
import {isDarkMode} from "../../App";
import PaymentSkeleton from "../../components/Skeletons/PaymentSkeleton";
import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {useToastContext} from "../../types/contexts/ToastContext";
import {setTitle} from "../../hooks/setTitle";
import {RootState} from "../../store";
import TopFXLogo from "../../assets/icons/TopFXLogo";
import {Input} from "@material-tailwind/react/components/Input";
import {ENV} from "../admin/Login";
import TotalFXLogo from "../../assets/icons/TotalFXLogo";
import {APILink} from "../../types/APILink";

export default function Payment() {
    const {transaction} = useParams();
    const handleError = useErrorHandler();
    const toast = useToastContext();
    const {t} = useTranslation();

    // const {showBoundary} = useErrorBoundary<AxiosError>();
    const [response, setResponse] = useState<TempTransactionResponse | null>(null);
    const [requestObj, setRequestObj] = useState<object>({});
    const [isInProgress, setInProgress] = useState<boolean>(false);
    const [showSkeleton, setShowSkeleton] = useState<boolean>(true);
    const [instructions, setInstructions] = useState<string>('');
    const dispatch = useDispatch();
    const errors = useSelector((state: RootState) => state.validation.errors);
    const [responseError, setResponseError] = useState<string>("");
    const [isCopied, setIsCopied] = useState<boolean>(false);
    const [logoImage, setLogoImage] = useState<string>('topfx');
    const isIframe = window.self !== window.top;

    const callbackPaymentFunction = useCallback((b: boolean) => {
        if (b) {
            API.payment(transaction, requestObj).then((response: AxiosResponse) => {
                setInProgress(false);
                const data = response.data.data as TransactionResponse;
                if (data.payment_page) {
                    window.location.replace(data.payment_page);
                }
                if (data.instructions) {
                    setInstructions(typeof data.instructions['html'] !== 'undefined' ? data.instructions['html'] : data.instructions)
                }
                setShowSkeleton(false);
            }).catch((e: AxiosError) => {
                // @ts-expect-error can be undefined
                setResponseError(e.response.data.message)
                setInProgress(false);
                API.removeURL(`post${APILink.PAYMENT.replace('$transactionID', transaction ?? '')}`)
            });
        } else setInProgress(false);
    }, [setInProgress, transaction, requestObj]);

    const payNow = useCallback(() => {
        if (!response)
            return;

        setInProgress(true);
        setResponseError('');
        dispatch(validateData({
            payload: {inputs: response.inputs, introducedData: requestObj, callback: callbackPaymentFunction}
        }));
    }, [response, requestObj, callbackPaymentFunction, dispatch]);

    useEffect(() => {
        setTitle(t('Payment') + " " + transaction);
        const env = process.env as unknown as ENV;
        setLogoImage(env.REACT_APP_LOGO);
        API.getTemporaryTransaction(transaction).then((response: AxiosResponse) => {
            const data = response.data.data as TempTransactionResponse;
            setResponse(data);
            if (data.method === 'WireTransfer' && !data.wire) {
                payNow();
            } else {
                setShowSkeleton(false);
            }
        }).catch(e => {
            handleError(e);
            API.removeURL(`get${APILink.PAYMENT.replace('$transactionID', transaction ?? '')}`)
        })
    }, [transaction,t,  dispatch, handleError, payNow]);

    const setLocalValue = useCallback((key: string, value: string | number) => {
        setRequestObj({...requestObj, [key as string]: value});
    }, [requestObj]);

    const generateUI = useMemo((): JSX.Element => {
        if (!response || response.inputs === null || instructions.length > 0)
            return <></>

        return <GenericInputs inputs={response?.inputs ?? []} setLocalValue={setLocalValue}/>
    }, [response, setLocalValue, instructions]);

    const copyAddress = useCallback(() => {
        if (!response || (!response.qr && !response.wire))
            return;

        const el = document.createElement("textarea");
        const text = response.qr ? 'client.payment.copied' : 'client.payment.wire_copied';
        if (el) {
            setIsCopied(true);
            const wrData = document.getElementById('wr_data');
            el.value = response.qr ? response.qr.accountWallet : (response.wire && wrData ? wrData.innerText.replaceAll('\n\n', '\n').trim() : '');
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {t(text)}</>, "bg-teal-500")
            setTimeout(() => setIsCopied(false), 5000);
        }
    }, [response, toast]);

    const payNowButton = useMemo(() => {
        if (!response || response.qr || response.wire || instructions.length > 0)
            return <></>
        return <Button onClick={() => payNow()} disabled={isInProgress} color="teal" size="md"
                       className="uppercase mt-4">{t('client.payment.pay_now')}</Button>
    }, [response, payNow, isInProgress, instructions]);

    const qrUI = useMemo(() => {
        if (!response || !response.qr || instructions.length > 0)
            return <></>
        return <>
            <div className="mt-4 text-center">
                <img src={response.qr.qr} className="bg-white inline-block w-48 h-48 rounded-md"/>
            </div>
        </>
    }, [response, instructions]);

    const wireUI = useMemo(() => {
        if ((!response || !response.wire) && instructions.length === 0)
            return <></>

        if (response && response.wire)
            return <div className="my-8" id="wr_data"
                        dangerouslySetInnerHTML={{__html: response.wire}}/>

        return <div className="my-8" id="wr_data"
                    dangerouslySetInnerHTML={{__html: instructions}}/>
    }, [response, instructions]);

    const infoUI = useMemo(() => {
        if (!response || !response.info)
            return <></>;

        return <div className="px-2 mt-8 w-full flex items-center justify-center">
            <div
                className="my-auto rounded-xl p-6 bg-transparent border border-[#b0bfc6] w-full max-w-xl text-sm"
                id="info_data"
            >
                <Typography className="text-teal-500 font-medium flex items-center">
                    <FontAwesomeIcon icon={solid('exclamation-circle')}
                                     className="text-teal-500 w-5 h-5 mr-2"/> {t('client.payment.notes')}
                </Typography>
                <div className="text-sm mt-4 text-gray-400 dark:text-blue-gray-400"
                     dangerouslySetInnerHTML={{__html: response.info.replace("\n", '<br/>')}}/>
            </div>
        </div>
    }, [response]);

    const amountUI = useMemo(() => {
        if (!response || !response.amount || !response.currency)
            return <></>

        if (!response.qr && !response.wire)
            return <label className="text-lg font-bold">{response.amount} {response.currency}</label>

        if (response.wire)
            return <Button onClick={() => copyAddress()} color="teal" size="md" className="uppercase py-2">
                <FontAwesomeIcon icon={solid('copy')} className="w-3 h-3"/>
            </Button>

        const parseCoin = response.currency.split('-');
        const currency = parseCoin[0];
        const protocol = parseCoin[1] ?? 'ERC20';
        return <div className="grid grid-col gap-6 mb-2" id="walletAddressQR">
            <Input
                crossOrigin={''}
                variant={'outlined'}
                label={t('client.wallet_address', {currency: response.currency})}
                type="text"
                value={response?.qr?.accountWallet}
                className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}
                icon={<FontAwesomeIcon icon={solid('copy')}
                                       className={`w-5 h-5 cursor-pointer ${isCopied ? 'bg-teal-800' : 'bg-teal-500 hover:bg-teal-800'} text-white p-2.5 rounded-[7px] z-10 border-solid border-1 border-gray-500`}
                                       onClick={() => copyAddress()}/>}
            />
            <Typography className="text-[11px] text-gray-600 font-normal text-left">{t('client.note', {
                currency,
                protocol
            })}</Typography>
        </div>
    }, [response, isCopied, t]);

    const errorUI = useMemo(() => {
        // @ts-expect-error can be undefined
        if (!errors || !responseError || errors.length === 0 || responseError.length === 0)
            return <></>;

        return <div className="my-6 text-center text-red-400">
            {responseError}
        </div>
    }, [errors, responseError]);

    const onClickBackToPlatform = useCallback((e: SyntheticEvent) => {
        e.preventDefault();
        if (!response)
            return;
        const url = `/payment/back/${transaction}`;
        if (window.location !== window.parent.location && !response.isIframe) {
            fetch(`${url}?action=clicked_back_button`).then(() => {
                window.parent.postMessage({
                    transaction,
                    action: 'clicked_back_button',
                }, '*');
                window.parent.postMessage("redirectDone", "*");
            });
        } else {
            window.location.replace(url);
        }
    }, [transaction, response]);

    const getLogoUI = useMemo(() => {
        if (logoImage !== 'totalfx')
            return <TopFXLogo className="mb-8 h-12 md:h-16 inline-block fixed top-2 left-2"
                              primaryColor={isDarkMode ? 'lightgray' : 'black'}/>;

        return <TotalFXLogo
            className="mb-8 h-12 md:h-16 inline-block fixed top-2 left-2"
            secondaryColor={isDarkMode ? 'lightgray' : '#041E68'}
        />
    }, [logoImage]);

    return (
        <div
            className={`${!response || !response.wire ? ' h-screen' : ''} ${response && response.info ? 'flex-col gap-2' : ''} flex justify-center items-center`}>
            {getLogoUI}

            <div
                className={`${!response || (!response.wire && !response.qr) ? 'w-full max-w-sm' : (isIframe ? 'w-fit py-8' : 'w-full max-w-lg py-8')} text-black dark:text-white rounded-xl text-center items-center px-2`}>
                {showSkeleton && <PaymentSkeleton/>}
                {!showSkeleton && response?.icon && !response?.qr ? <img alt={response?.method} src={response.icon}
                                                                         className="inline-block h-16 mb-8 rounded-lg"/> : null}
                {!showSkeleton ? <div className="relative rounded-xl p-6 bg-white dark:bg-blue-gray-900">
                    <div className="mb-6 flex items-center w-full justify-center">
                        {amountUI}
                    </div>
                    {generateUI}
                    {errorUI}
                    {payNowButton}
                    {qrUI}
                    {wireUI}

                    <div className="text-center mt-1 mb-2">
                        <a onClick={onClickBackToPlatform} href="#"
                           className="text-xs text-gray-400 dark:text-blue-gray-400">{t('client.payment.back_to_platform')}
                        </a>
                    </div>
                </div> : null}
            </div>
            {infoUI}
        </div>
    );
}
